import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SearchOutlined, DeleteFilled, PlusOutlined } from '@ant-design/icons';
import { Button, Tooltip, Alert, ModalFuncProps } from 'antd';
import classNames from 'classnames';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { VirtualItem } from '@tanstack/react-virtual';
import { CustomWidgetVariant } from '@kemu-io/kemu-types';
import { SerializableServiceInfo, KemuHubFunctions, UninstallServiceArgs, UninstallServiceHubResponse, ServiceWidgetInfo } from '@kemu-io/hs-types';
import { DEFAULT_THING_ID } from '@kemu-io/kemu-core/common/constants';
import { compareVersions } from 'compare-versions';
import { selectUserWidgets, WidgetCollectionItem } from '@src/app/reducers/widget/widgetSlice';
import { deleteWidgetAction } from '@src/app/reducers/widget/deleteWidgetReducer';
import { selectCurrentRecipePoolId, selectedBlock, selectVisibleGroup } from '@src/features/Workspace/workspaceSlice';
import { selectWidgetLauncherTargetPosition } from '@src/features/interface/interfaceSlice';
import { addWidgetFromTemplateAction, addHubServiceWidgetAction } from '../logicMapperSlice';
import { pasteFromClipboardAction } from '../reducers/clipboardReducers';
import { getDecodedClipboardData } from '../reducers/clipboardUtils';
import { useHubContext } from '@common/context/HubContext/HubContext';
import useTranslation from '@common/hooks/useTranslation';
import useAlert from '@components/alert/useAlert';
import useGlobalNotification from '@hooks/useGlobalNotification';
import RoundedModal from '@components/roundedModal/roundedModal';
import CustomWidgetIcon from './components/CustomWidgetIcon/CustomWidgetIcon';
import BaseServiceIcon from './components/BaseServiceIcon/BaseServiceIcon';
import ServiceCreationModal, { findProjectCreatorService } from './modals/ServiceCreationModal/ServiceCreationModal';
import ServiceInstallerModal, { findServiceInstaller } from './modals/ServiceInstallerModal/ServiceInstallerModal';
import { ReactComponent as InstallIcon } from './assets/install-icon.svg';
import styles from './WidgetLauncher.module.css';

type Props = {
	visible: boolean;
	onClose: () => void;
};

type LauncherItemType =
	| 'command'
	| 'custom-widget'
	| 'hub-service-group'
	| 'hub-service-default'
	| 'hub-service-variant'
	| 'hub-service-custom-widget'
	| 'section-header';

type LauncherCommandId = 'create-service' | 'install-service';

type LauncherCommand = {
	/** Stable unique id for the command. */
	id: LauncherCommandId;
	/** User-facing label. */
	name: string;
	/** User-facing description. */
	description: string;
	/** Optional icon. */
	icon?: React.ReactNode;
	/** Whether the command should be shown. */
	enabled: boolean;
	/** Runs the command. */
	run: () => void;
};

type LauncherItem = {
	id: string;
	type: LauncherItemType;
	name: string;
	description: string;
	selectable: boolean;
	// For custom widgets
	widget?: WidgetCollectionItem;
	// For hub services
	service?: SerializableServiceInfo;
	variantId?: string;
	// For indentation (variants are children)
	isVariant?: boolean;
	parentServiceName?: string;
	parentId?: string;
	// For commands
	command?: LauncherCommand;
	// For service custom widgets (widget blueprints provided by a service)
	serviceWidget?: ServiceWidgetInfo;
};

// const { confirm } = Modal;

const OVERSCAN = 8;

/**
 * Finds the first selectable index in a list.
 * @param items the list of launcher items
 * @returns the first selectable index, or 0 if none are selectable
 */
const getFirstSelectableIndex = (items: LauncherItem[]): number => {
	const idx = items.findIndex((i) => i.selectable);
	return idx >= 0 ? idx : 0;
};

/**
 * Finds the next selectable index in a list.
 * @param items the list of launcher items
 * @param startIndex the starting index
 * @param direction the direction to move (1 for down, -1 for up)
 * @returns the next selectable index (wraps), or the startIndex if none are selectable
 */
const getNextSelectableIndex = (items: LauncherItem[], startIndex: number, direction: 1 | -1): number => {
	if (items.length === 0) { return 0; }
	const hasSelectable = items.some((i) => i.selectable);
	if (!hasSelectable) { return startIndex; }

	let idx = startIndex;
	for (let i = 0; i < items.length; i++) {
		idx = (idx + direction + items.length) % items.length;
		if (items[idx]?.selectable) { return idx; }
	}
	return startIndex;
};

const WidgetLauncher = (props: Props): React.JSX.Element => {
	const { visible, onClose } = props;
	const dispatch = useDispatch();
	const t = useTranslation('WidgetLauncher');
	const cw = useTranslation('CommonWords');
	const alert = useAlert();
	const userWidgets = useSelector(selectUserWidgets);
	const recipePoolId = useSelector(selectCurrentRecipePoolId);
	const currentBlock = useSelector(selectedBlock);
	const currentFolder = useSelector(selectVisibleGroup);
	const targetPosition = useSelector(selectWidgetLauncherTargetPosition);
	const { services, connector } = useHubContext();
	const { showNotification } = useGlobalNotification();

	const [filterTerm, setFilterTerm] = useState('');
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);
	const [resultsEl, setResultsEl] = useState<HTMLDivElement | null>(null);
	const [showCreationModal, setShowCreationModal] = useState(false);
	const [showInstallerModal, setShowInstallerModal] = useState(false);

	// Get custom widgets (excluding bundles)
	const customWidgets = useMemo(() => {
		return userWidgets.filter(widget => widget.variant !== CustomWidgetVariant.Bundle);
	}, [userWidgets]);

	// Get usable hub services (excluding internal)
	const usableServices = useMemo(() => {
		return services.available.filter((service) => !service.internal);
	}, [services.available]);

	const projectCreatorAvailable = useMemo(() => {
		return !!findProjectCreatorService(services.available);
	}, [services.available]);

	const serviceInstallerAvailable = useMemo(() => {
		return !!findServiceInstaller(services.available);
	}, [services.available]);

	const handleOpenCreateService = useCallback(() => {
		setShowCreationModal(true);
	}, []);

	const handleOpenInstallService = useCallback(() => {
		setShowInstallerModal(true);
	}, []);

	const handleCloseCreateService = useCallback(() => {
		setShowCreationModal(false);
	}, []);

	const handleCloseInstallService = useCallback(() => {
		setShowInstallerModal(false);
	}, []);

	/**
	 * Creates the command list for the launcher (reusable approach for adding more commands later).
	 * @returns the list of enabled commands
	 */
	const commands = useMemo((): LauncherCommand[] => {
		const list: LauncherCommand[] = [
			{
				id: 'create-service',
				name: t('Command.CreateService.Name', 'Create Service'),
				description: t('Command.CreateService.Description', 'Generate a new Hub Service project'),
				icon: <PlusOutlined />,
				enabled: projectCreatorAvailable,
				run: handleOpenCreateService,
			},
			{
				id: 'install-service',
				name: t('Command.InstallService.Name', 'Install Service'),
				description: t('Command.InstallService.Description', 'Install a Hub Service from a zip file'),
				icon: <InstallIcon />,
				enabled: serviceInstallerAvailable,
				run: handleOpenInstallService,
			},
		];

		return list.filter((c) => c.enabled);
	}, [
		t,
		projectCreatorAvailable,
		serviceInstallerAvailable,
		handleOpenCreateService,
		handleOpenInstallService,
	]);

	// Normalize all items into a flat list
	const allItems = useMemo((): LauncherItem[] => {
		const items: LauncherItem[] = [];

		// Add commands
		commands.forEach((command) => {
			items.push({
				id: `command-${command.id}`,
				type: 'command',
				name: command.name,
				description: command.description,
				selectable: true,
				command,
			});
		});

		// Add custom widgets
		customWidgets.forEach((widget) => {
			items.push({
				id: `custom-${widget.dbId}`,
				type: 'custom-widget',
				name: widget.name,
				description: widget.description || '',
				selectable: true,
				widget,
			});
		});

		// Add hub services (group header + selectable sub-items)
		usableServices.forEach((service) => {
			const serviceName = service.title || service.shortTitle || service.name.split('.').pop() || service.name;
			const groupId = `service-group-${service.name}-${service.version}`;
			const hasVariants = (service.variants || []).length > 0;
			const hasCustomWidgets = (service.customWidgets || []).length > 0;
			const hasSubItems = hasVariants || hasCustomWidgets;

			if (hasSubItems) {
				// Non-selectable header row (matches Launchpad "folder" behavior)
				items.push({
					id: groupId,
					type: 'hub-service-group',
					name: serviceName,
					description: service.description || '',
					selectable: false,
					service,
				});

				// Default/base service is rendered as a sub-item (uses defaultVariantInfo when present)
				items.push({
					id: `service-default-${service.name}-${service.version}`,
					type: 'hub-service-default',
					name: service.defaultVariantInfo?.shortTitle || serviceName,
					description: service.defaultVariantInfo?.description || service.description || '',
					selectable: true,
					service,
					isVariant: true,
					parentServiceName: serviceName,
					parentId: groupId,
				});

				// Variants as indented children
				(service.variants || []).forEach((variant) => {
					items.push({
						id: `variant-${service.name}-${service.version}-${variant.id}`,
						type: 'hub-service-variant',
						name: variant.name || serviceName,
						description: variant.description || service.description || '',
						selectable: true,
						service,
						variantId: variant.id,
						isVariant: true,
						parentServiceName: serviceName,
						parentId: groupId,
					});
				});

				// Service custom widgets (widget blueprints bundled with the service)
				(service.customWidgets || []).forEach((customWidget) => {
					items.push({
						id: `service-widget-${service.name}-${service.version}-${customWidget.rootGroupId}`,
						type: 'hub-service-custom-widget',
						name: customWidget.name,
						description: customWidget.description || '',
						selectable: true,
						service,
						serviceWidget: customWidget,
						isVariant: true,
						parentServiceName: serviceName,
						parentId: groupId,
					});
				});
			} else {
				// No variants: render as a normal selectable service
				items.push({
					id: `service-default-${service.name}-${service.version}`,
					type: 'hub-service-default',
					name: serviceName,
					description: service.description || '',
					selectable: true,
					service,
				});
			}
		});

		return items;
	}, [commands, customWidgets, usableServices]);

	// Filter items based on search term
	const filteredItems = useMemo(() => {
		if (!filterTerm.trim()) {
			return allItems;
		}

		const searchLower = filterTerm.toLowerCase();
		const matches = (item: LauncherItem) => {
			const nameMatch = item.name.toLowerCase().includes(searchLower);
			const descMatch = item.description.toLowerCase().includes(searchLower);
			const parentMatch = item.parentServiceName?.toLowerCase().includes(searchLower);
			return nameMatch || descMatch || parentMatch;
		};

		const byId = new Map(allItems.map((i) => [i.id, i] as const));
		const includeIds = new Set<string>();

		// If a group matches, include it and all its children.
		allItems
			.filter((i) => i.type === 'hub-service-group' && matches(i))
			.forEach((group) => {
				includeIds.add(group.id);
				allItems
					.filter((child) => child.parentId === group.id)
					.forEach((child) => includeIds.add(child.id));
			});

		// Include matching non-group items and their parent groups (for context).
		allItems
			.filter((i) => i.type !== 'hub-service-group' && matches(i))
			.forEach((item) => {
				includeIds.add(item.id);
				if (item.parentId && byId.has(item.parentId)) {
					includeIds.add(item.parentId);
				}
			});

		return allItems.filter((i) => includeIds.has(i.id));
	}, [allItems, filterTerm]);

	// Sort items: custom widgets first (by version), then hub services (already grouped)
	const sortedFilteredItems = useMemo(() => {
		const commandItems = filteredItems.filter(item => item.type === 'command');
		const customWidgetItems = filteredItems.filter(item => item.type === 'custom-widget');
		const hubServiceItems = filteredItems.filter(item => item.type !== 'custom-widget' && item.type !== 'command');

		// Sort custom widgets by version (descending, V2 first)
		const sortedCustomWidgets = customWidgetItems.sort((a, b) => {
			return compareVersions(b.widget?.protocolVersion || '1', a.widget?.protocolVersion || '1');
		});

		// Commands appear as their own section at the bottom of the list.
		return [...sortedCustomWidgets, ...hubServiceItems, ...commandItems];
	}, [filteredItems]);

	/**
	 * Adds section header rows so we can render a single virtualized list while preserving the UI sections.
	 * @returns the flat list of items with headers included
	 */
	const virtualItems = useMemo((): LauncherItem[] => {
		const commandList = sortedFilteredItems.filter((i) => i.type === 'command');
		const custom = sortedFilteredItems.filter((i) => i.type === 'custom-widget');
		const hub = sortedFilteredItems.filter((i) => i.type !== 'custom-widget' && i.type !== 'command');

		const items: LauncherItem[] = [];
		if (custom.length > 0) {
			items.push({
				id: 'section-custom-widgets',
				type: 'section-header',
				name: t('Section.CustomWidgets', 'Custom Widgets'),
				description: '',
				selectable: false,
			});
			items.push(...custom);
		}

		if (hub.length > 0) {
			items.push({
				id: 'section-hub-services',
				type: 'section-header',
				name: t('Section.HubServices', 'Hub Services'),
				description: '',
				selectable: false,
			});
			items.push(...hub);
		}

		if (commandList.length > 0) {
			items.push({
				id: 'section-commands',
				type: 'section-header',
				name: t('Section.Commands', 'Commands'),
				description: '',
				selectable: false,
			});
			items.push(...commandList);
		}

		return items;
	}, [sortedFilteredItems, t]);

	// Reset selection when filter changes
	useEffect(() => {
		setSelectedIndex(getFirstSelectableIndex(virtualItems));
	}, [filterTerm, virtualItems]);

	const selectedItemId = useMemo(() => {
		return virtualItems[selectedIndex]?.id;
	}, [virtualItems, selectedIndex]);

	const rowVirtualizer = useVirtualizer({
		count: virtualItems.length,
		// Use a state-backed ref so the virtualizer re-initializes when the element mounts (avoids initial layout gaps).
		getScrollElement: () => (visible ? resultsEl : null),
		// Section headers are much shorter than normal rows, so estimate them separately to prevent large initial gaps.
		estimateSize: (index) => (virtualItems[index]?.type === 'section-header' ? 43 : 84),
		overscan: OVERSCAN,
	});

	// Ensure the virtualizer measures correctly when the modal first opens (antd modal animations can briefly report 0px height).
	useEffect(() => {
		if (!visible) { return; }
		const rafId = requestAnimationFrame(() => {
			rowVirtualizer.measure();
		});
		const timeoutId = setTimeout(() => {
			rowVirtualizer.measure();
		}, 50);

		return () => {
			cancelAnimationFrame(rafId);
			clearTimeout(timeoutId);
		};
	}, [visible, rowVirtualizer]);

	// Keep the selected item in view when navigating with the keyboard.
	useEffect(() => {
		if (!visible) { return; }
		if (!selectedItemId) { return; }

		// Prefer "nearest" scroll behavior to avoid snapping the selection to the top on each arrow press.
		// If the selected row isn't currently rendered (virtualized out), fall back to the virtualizer.
		const container = resultsEl;
		if (container) {
			const selectedEl = container.querySelector(`[data-item-id="${selectedItemId}"]`);
			if (selectedEl && 'scrollIntoView' in selectedEl) {
				(selectedEl as HTMLElement).scrollIntoView({ block: 'nearest' });
				return;
			}
		}

		rowVirtualizer.scrollToIndex(selectedIndex, { align: 'auto' });
	}, [visible, selectedItemId, selectedIndex, rowVirtualizer, resultsEl]);

	// Auto-focus input when modal opens
	useEffect(() => {
		if (visible && inputRef) {
			setTimeout(() => {
				inputRef?.focus();
			}, 100);
		} else {
			setFilterTerm('');
			setSelectedIndex(getFirstSelectableIndex(virtualItems));
			rowVirtualizer.scrollToOffset(0);
		}
	}, [visible, virtualItems, inputRef]);

	// Add the selected item
	/**
	 * Adds a launcher item to the current recipe/folder context.
	 * @param item the launcher item to add
	 * @returns void
	 */
	const addLauncherItem = useCallback((item: LauncherItem) => {
		if (!item.selectable) { return; }

		if (item.type === 'command' && item.command) {
			item.command.run();
			onClose();
			return;
		}

		if (!recipePoolId || !currentBlock?.recipeId) { return; }

		const thingId = currentBlock.recipeId;
		const currentGroupId = currentFolder?.groupId;

		if (item.type === 'custom-widget' && item.widget) {
			dispatch(addWidgetFromTemplateAction({
				widget: item.widget,
				recipeId: recipePoolId,
				thingId,
				currentGroupId,
				position: targetPosition || undefined,
			}));
			onClose();
			return;
		}

		if (item.type === 'hub-service-default' && item.service) {
			dispatch(addHubServiceWidgetAction({
				service: item.service,
				currentGroupId,
				sessionId: item.service.sessionId,
				position: targetPosition || undefined,
			}));
			onClose();
			return;
		}

		if (item.type === 'hub-service-variant' && item.service) {
			dispatch(addHubServiceWidgetAction({
				service: item.service,
				variantId: item.variantId,
				currentGroupId,
				sessionId: item.service.sessionId,
				position: targetPosition || undefined,
			}));
			onClose();
			return;
		}

		if (item.type === 'hub-service-custom-widget' && item.serviceWidget) {
			const decodedContents = getDecodedClipboardData(item.serviceWidget.contents);
			if (!decodedContents) { return; }

			dispatch(pasteFromClipboardAction({
				mouseLocation: targetPosition || undefined,
				recipeId: recipePoolId,
				thingId: DEFAULT_THING_ID,
				mapData: decodedContents,
			}));
			onClose();
		}
	}, [recipePoolId, currentBlock, currentFolder, dispatch, onClose, targetPosition]);

	/**
	 * Adds whatever item is currently keyboard-selected.
	 * @returns void
	 */
	const handleAddSelected = useCallback(() => {
		if (virtualItems.length === 0) { return; }
		const item = virtualItems[selectedIndex];
		if (!item) { return; }
		addLauncherItem(item);
	}, [addLauncherItem, virtualItems, selectedIndex]);

	// Handle keyboard navigation
	const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
		if (!visible) { return; }

		switch (event.key) {
			case 'ArrowDown':
				event.preventDefault();
				setSelectedIndex((prev) => getNextSelectableIndex(virtualItems, prev, 1));
				break;
			case 'ArrowUp':
				event.preventDefault();
				setSelectedIndex((prev) => getNextSelectableIndex(virtualItems, prev, -1));
				break;
			case 'Enter':
				event.preventDefault();
				handleAddSelected();
				break;
			case 'Escape':
				event.preventDefault();
				onClose();
				break;
		}
	}, [visible, virtualItems, handleAddSelected, onClose]);

	const handleFilterChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setFilterTerm(event.target.value);
	}, []);

	const handleItemClick = useCallback((index: number) => {
		const item = virtualItems[index];
		if (!item || !item.selectable) { return; }
		setSelectedIndex(index);
		addLauncherItem(item);
	}, [addLauncherItem, virtualItems]);

	const handleDeleteCustomWidget = useCallback((event: React.MouseEvent, widget: WidgetCollectionItem) => {
		event.stopPropagation();
		alert.confirm({
			title: t('DeleteModal.Title', 'Delete Custom Widget?'),
			content: t(
				'Description',
				'You are about to delete "{name}". This process cannot be undone.',
				{ name: widget.name }
			),
			okText: t('DeleteModal.OkButton', 'Delete'),
			cancelText: t('DeleteModal.CancelButton', 'Cancel'),
			okButtonColor: 'danger',
			iconColor: 'danger',
			onOk: () => {
				dispatch(deleteWidgetAction({
					version: widget.version,
					widgetDbId: widget.dbId,
				}));
			}
		});
	}, [dispatch, t, alert]);

	const handleUninstallService = useCallback((event: React.MouseEvent, serviceName: string, version: string) => {
		event.stopPropagation();
		
		const modalProps: ModalFuncProps & { okButtonColor?: string, iconColor?: string } = {
			title: t('Uninstall.Confirm.Title', 'Uninstall Service?'),
			// icon: <ExclamationCircleFilled />,
			content: t('Uninstall.Confirm.Description', 'Are you sure you want to uninstall this service?'),
			okButtonColor: 'danger',
			iconColor: 'danger',
			okText: cw('Yes'),
			cancelText: cw('No'),
		};

		// @ts-expect-error useAlert returns a modal instance but definition says void
		const thisModal = alert.confirm({
			...modalProps,
			onOk: async () => {
				try {
					thisModal.update({
						...modalProps,
						okButtonProps: { 
							loading: true,
						},
						cancelButtonProps: { disabled: true, loading: false },
					});

					const args: UninstallServiceArgs = [{
						serviceName: serviceName,
						version,
					}];

					const result = await connector.executeHubFunction<UninstallServiceHubResponse>(KemuHubFunctions.UninstallService, args, {
						timeout: 5 * 60 * 1000, // 5 minutes
					});

					if (result) {
						showNotification({
							type: 'success',
							title: t.withBaseKey('CommonEvents.ServiceUninstalled')('Title', 'Service Uninstalled').toString(),
							message: t.withBaseKey('CommonEvents.ServiceUninstalled')('Message', 'Service "{service}" was uninstalled successfully.', { service: result[0].service.name }).toString(),
							duration: 5,
						});
					}
				} catch (e) {
					console.log('Error removing service: ', e);
					// Re turn a promise that never resolves to prevent the modal from closing
					return new Promise(() => {
						thisModal.update({
							...modalProps,
							title: t('Uninstall.ErrorTitle', 'Error Uninstalling Service'),
							content: <Alert
								type='error'
								description={(e as any).message || (e as any).error || typeof e === 'string' ? e : t('Uninstall.UnknownError', 'An unknown error occurred')}
							/>,
							okButtonProps: { 
								loading: false,
							},
						});
					});
				}
			},
		});
	}, [t, cw, connector, showNotification, alert]);

	const renderItemIcon = (item: LauncherItem, isSelected: boolean) => {
		if (item.type === 'section-header') {
			return null;
		}

		if (item.type === 'command' && item.command) {
			return (
				<div className={styles.CommandIcon} data-selected={isSelected ? 'true' : 'false'}>
					{item.command.icon}
				</div>
			);
		}

		if (item.type === 'custom-widget' && item.widget) {
			return (
				<CustomWidgetIcon
					icon={item.widget.icon}
					color={item.widget.color}
				/>
			);
		}

		if (item.type === 'hub-service-custom-widget' && item.serviceWidget) {
			return (
				<CustomWidgetIcon
					icon={item.serviceWidget.icon}
					color={item.serviceWidget.color}
					devMode={item.service?.devMode}
				/>
			);
		}

		if ((item.type === 'hub-service-group' || item.type === 'hub-service-default' || item.type === 'hub-service-variant') && item.service) {
			let icon = item.service.svgIcon;
			let color = item.service.color;

			if (item.type === 'hub-service-variant' && item.variantId) {
				const variant = item.service.variants?.find(v => v.id === item.variantId);
				if (variant) {
					icon = variant.svgIcon || icon;
					color = variant.color || color;
				}
			}

			return (
				<BaseServiceIcon
					svgIcon={icon}
					color={color}
					selected={isSelected}
					className={styles.ServiceIconOverride}
					devMode={item.service.devMode}
				/>
			);
		}

		return null;
	};

	return (
		<>
			<RoundedModal
				visible={visible}
				onCancel={onClose}
				title={t('Title', 'What would you like to add?')}
				width={600}
				centered
				closable
				noFooter
				closeOnMaskClick
			>
				<div className={styles.Container} tabIndex={-1}>
				{/* Search input */}
				<div className={styles.SearchContainer}>
					<SearchOutlined className={styles.SearchIcon} />
					<input
						ref={setInputRef}
						type="text"
						className={styles.SearchInput}
						placeholder={t('SearchPlaceholder', 'What would you like to add? (e.g. \'Stripe\', \'Chart\', \'Database\')')}
						value={filterTerm}
						onChange={handleFilterChange}
						onKeyDown={handleKeyDown}
					/>
					<button
						type="button"
						className={styles.EscButton}
						onClick={onClose}
						title={t('EscButton', 'ESC')}
					>
						ESC
					</button>
				</div>

				{/* Results list */}
				<div
					ref={setResultsEl}
					className={styles.ResultsContainer}
				>
					{virtualItems.length > 0 ? (
						<div
							className={styles.List}
							style={{
								height: rowVirtualizer.getTotalSize(),
								position: 'relative',
								width: '100%',
							}}
						>
							{rowVirtualizer.getVirtualItems().map((virtualRow: VirtualItem) => {
								const item = virtualItems[virtualRow.index];
								const globalIndex = virtualRow.index;
								const isSelected = globalIndex === selectedIndex;
								const isGroup = item?.type === 'hub-service-group';
								const isSectionHeader = item?.type === 'section-header';

								if (!item) { return null; }

								return (
									<div
										key={virtualRow.key}
										ref={rowVirtualizer.measureElement}
										data-index={virtualRow.index}
										data-item-id={item.id}
										style={{
											position: 'absolute',
											top: 0,
											left: 0,
											width: '100%',
											transform: `translateY(${virtualRow.start}px)`,
										}}
									>
										<div
											className={classNames(styles.Item, {
												[styles.Selected]: isSelected,
												[styles.Variant]: item.isVariant,
												[styles.Group]: isGroup,
												[styles.SectionHeaderRow]: isSectionHeader,
											})}
											onClick={() => handleItemClick(globalIndex)}
										>
											{!isSectionHeader && (
												<div className={styles.ItemIcon}>
													{renderItemIcon(item, isSelected)}
												</div>
											)}

											<div className={styles.ItemContent}>
												{isSectionHeader ? (
													<div className={styles.SectionHeaderText}>{item.name}</div>
												) : (
													<>
														<div className={styles.ItemName}>{item.name}</div>
														<div className={styles.ItemDescription}>{item.description}</div>
													</>
												)}
											</div>

											{!isSectionHeader && item.widget?.protocolVersion && (
												<div className={styles.ItemMeta}>v{item.widget.version}</div>
											)}

											{!isSectionHeader && item.service && (isGroup || (item.type === 'hub-service-default' && !item.isVariant)) && (
												<div className={styles.ItemMeta}>{item.service.version}</div>
											)}

											{!isSectionHeader && item.widget && (
												<Tooltip title={t('DeleteWidget', 'Delete Widget')}>
													<Button
														type="text"
														icon={<DeleteFilled />}
														className={styles.DeleteButton}
														onClick={(e) => handleDeleteCustomWidget(e, item.widget!)}
													/>
												</Tooltip>
											)}

											{!isSectionHeader && item.service && (isGroup || (item.type === 'hub-service-default' && !item.isVariant)) && (
												<Tooltip title={t('UninstallService', 'Uninstall Service')}>
													<Button
														type="text"
														icon={<DeleteFilled />}
														className={styles.DeleteButton}
														onClick={(e) => handleUninstallService(e, item.service!.name, item.service!.version)}
													/>
												</Tooltip>
											)}
										</div>
									</div>
								);
							})}
						</div>
					) : (
						<div className={styles.EmptyState}>
							{t('EmptyState', 'No items found')}
						</div>
					)}
				</div>

					{/* Footer hints */}
					<div className={styles.Footer}>
						<span>{t('Footer.Navigate', '↑↓ to navigate')}</span>
						<span>{t('Footer.Add', 'Enter to add')}</span>
					</div>
				</div>
			</RoundedModal>

			<ServiceCreationModal
				visible={showCreationModal}
				onClose={handleCloseCreateService}
			/>

			<ServiceInstallerModal
				visible={showInstallerModal}
				onClose={handleCloseInstallService}
			/>
		</>
	);
};

export default WidgetLauncher;
